// TelcoTrack - Excavation Material Constants
// Materiál výkopu pro `VYKOP` (při přechodu Nevykopaný → Vykopaný)
// Sjednoceno s tabulkou povrchů pro nacenění výkopu.

// =============================================================================
// MAIN CATEGORIES (hlavní kategorie)
// =============================================================================

export const EXCAVATION_MATERIALS = {
  VOLNY_TEREN: 'VOLNY_TEREN',
  CHODNIK: 'CHODNIK',
  KACIREK: 'KACIREK',
  VOZOVKA: 'VOZOVKA',
  /** Podvrt – rozlišení řízený / neřízený na hlavní trase VYKOP; ne samostatný objekt typu PODVRT */
  POVRCH_PODVRT: 'POVRCH_PODVRT',
} as const;

export type ExcavationMaterial = (typeof EXCAVATION_MATERIALS)[keyof typeof EXCAVATION_MATERIALS];

// =============================================================================
// SUBTYPES (podtypy)
// =============================================================================

/** Volný terén */
export const EXCAVATION_SUBTYPES_VOLNY_TEREN = ['35x70', '35x100'] as const;

/** Chodník */
export const EXCAVATION_SUBTYPES_CHODNIK = ['ASFALT', 'BETON', 'DLAZBA', 'MOZAIKA'] as const;

/** Vozovka */
export const EXCAVATION_SUBTYPES_VOZOVKA = ['ASFALT', 'BETON', 'DLAZBA', 'STERK'] as const;

/** Podvrt (podskupina materiálu POVRCH_PODVRT) */
export const EXCAVATION_SUBTYPES_PODVRT = ['RIDENY', 'NERIDENY'] as const;

/** Kačírek – žádný podtyp */
export const EXCAVATION_MATERIALS_WITHOUT_SUBTYPE: ExcavationMaterial[] = [EXCAVATION_MATERIALS.KACIREK];

export type ExcavationMaterialSubtype =
  | (typeof EXCAVATION_SUBTYPES_VOLNY_TEREN)[number]
  | (typeof EXCAVATION_SUBTYPES_CHODNIK)[number]
  | (typeof EXCAVATION_SUBTYPES_VOZOVKA)[number]
  | (typeof EXCAVATION_SUBTYPES_PODVRT)[number];

// =============================================================================
// Režim ceníku výkopů: 'linear' (délkové metry m) vs. 'volume' (m³ / m²)
// =============================================================================

export type ExcavationPricingMode = 'linear' | 'volume';

export const EXCAVATION_PRICING_MODES = {
  LINEAR: 'linear',
  VOLUME: 'volume',
} as const satisfies Record<string, ExcavationPricingMode>;

export function normalizeExcavationPricingMode(raw: unknown): ExcavationPricingMode {
  if (raw === 'volume' || raw === 1 || raw === '1' || raw === true) return 'volume';
  return 'linear';
}

// =============================================================================
// Ceník nový — konsolidované billing buckety (Výkop PHASE.1 / Obnova PHASE.2)
// =============================================================================

/** Billing bucket pro ceník nový (m²/m³ položky výkopu a obnovy). */
export const EXCAVATION_SURFACE_BUCKETS = {
  VOLNY_TEREN: 'VOLNY_TEREN',
  ASFALT_A_BETON: 'ASFALT_A_BETON',
  DLAZBA: 'DLAZBA',
  MOZAIKA: 'MOZAIKA',
  KACIREK: 'KACIREK',
  STERK: 'STERK',
} as const;

export type ExcavationSurfaceBucket =
  (typeof EXCAVATION_SURFACE_BUCKETS)[keyof typeof EXCAVATION_SURFACE_BUCKETS];

/** Který modal / billing fáze segment pochází z (dual Povrchy UI). */
export type ExcavationSurfacePhaseScope = 'vykop' | 'obnova';

export const EXCAVATION_SURFACE_BUCKET_NAMES: Record<ExcavationSurfaceBucket, string> = {
  [EXCAVATION_SURFACE_BUCKETS.VOLNY_TEREN]: 'Volný terén',
  [EXCAVATION_SURFACE_BUCKETS.ASFALT_A_BETON]: 'Asfalt a beton',
  [EXCAVATION_SURFACE_BUCKETS.DLAZBA]: 'Zámková a ostatní dlažba',
  [EXCAVATION_SURFACE_BUCKETS.MOZAIKA]: 'Mozaika',
  [EXCAVATION_SURFACE_BUCKETS.KACIREK]: 'Kačírek',
  [EXCAVATION_SURFACE_BUCKETS.STERK]: 'Štěrk a asfaltová drť',
};

/** Jedna položka Nad rámec u povrchu (kód ceníku + šířka; délka = `lengthM` úseku). */
export interface ExcavationBeyondScopeRow {
  code: string;
  widthM: number;
}

/** Jedna položka rozpadu trati na povrchy (např. 70 m volný terén + 20 m chodník asfalt). */
export interface ExcavationSurfaceSegment {
  excavationMaterial: string;
  excavationMaterialSubtype?: string;
  lengthM: number;
  /** Ručně zadaná šířka (m) — přednost před profilem bucketu pro m²/m³. */
  widthM?: number;
  /** Ručně zadaná hloubka (m) — pro m³ položky (objem výkopu / hlíny). */
  depthM?: number;
  /** Délka krytu zpevněného povrchu (m) v režimu volume, pokud se zadává odděleně od objemu výkopu. */
  surfaceLengthM?: number;
  /** Šířka krytu zpevněného povrchu (m) v režimu volume, pokud se zadává odděleně od objemu výkopu. */
  surfaceWidthM?: number;
  /** Délka objemu výkopu / hlíny (m) v režimu volume, pokud se zadává odděleně od krytu povrchu. */
  volumeLengthM?: number;
  /** Šířka objemu výkopu / hlíny (m) v režimu volume, pokud se zadává odděleně od krytu povrchu. */
  volumeWidthM?: number;
  /** Explicitní m² / m³ — přednost před výpočtem z rozměrů. */
  m2?: number;
  m3?: number;
  /** Konsolidovaný billing bucket (ceník nový); lze dopočítat z materiálu + podtypu. */
  surfaceBucket?: ExcavationSurfaceBucket;
  /** Zdroj modalu: výkop (PHASE.1) vs obnova (PHASE.2) — pro dual Povrchy UI. */
  phaseScope?: ExcavationSurfacePhaseScope;
  /**
   * Prohloubení tohoto povrchu v cm (krok 10, 0–200). Per-povrch: každý úsek může mít jiné prohloubení
   * (nebo žádné = 0/undefined). Nahrazuje dřívější jediné `excavationDeepeningCm` na celý výkop.
   */
  deepeningCm?: number;
  /**
   * @deprecated Prefer `removalBeyondScopeRows` when a surface has multiple Nad rámec entries.
   * Still mirrored as the first row for Android / legacy readers.
   */
  removalBeyondScope?: string;
  /** @deprecated Prefer `removalBeyondScopeRows`. Mirrored from the first row. */
  removalBeyondScopeWidthM?: number;
  /** Více položek Nad rámec u jednoho povrchu (asfalt odstranění / finální obnova). */
  removalBeyondScopeRows?: ExcavationBeyondScopeRow[];
}

/** Bourání betonových základů / rozvoz zemina — vždy viditelné řádky (ne pod per-řádkovým „Nad rámec“). */
export interface SpecialRemovalRow {
  code: string;
  lengthM: number;
  widthM: number;
  /** Jen u BOURANI_BETON_ZAKLADY (m³). */
  depthM?: number;
}

// =============================================================================
// DISPLAY NAMES (české názvy)
// =============================================================================

export const EXCAVATION_MATERIAL_NAMES: Record<string, string> = {
  [EXCAVATION_MATERIALS.VOLNY_TEREN]: 'Volný terén',
  [EXCAVATION_MATERIALS.CHODNIK]: 'Chodník',
  [EXCAVATION_MATERIALS.KACIREK]: 'Kačírek',
  [EXCAVATION_MATERIALS.VOZOVKA]: 'Vozovka',
  [EXCAVATION_MATERIALS.POVRCH_PODVRT]: 'Podvrt',
};

export const EXCAVATION_SUBTYPE_NAMES: Record<string, string> = {
  '35x70': '35x70 cm',
  '35x100': '35x100 cm',
  ASFALT: 'Asfalt',
  BETON: 'Beton',
  DLAZBA: 'Dlažba',
  MOZAIKA: 'Mozaika',
  STERK: 'Štěrk',
  RIDENY: 'Podvrt řízený',
  NERIDENY: 'Podvrt neřízený',
};

// =============================================================================
// HELPERS
// =============================================================================

/** Vrátí podtypy pro danou hlavní kategorii; prázdné pro Kačírek */
export function getSubtypesForMaterial(
  material: ExcavationMaterial
): readonly string[] {
  if (material === EXCAVATION_MATERIALS.VOLNY_TEREN) {
    return EXCAVATION_SUBTYPES_VOLNY_TEREN;
  }
  if (material === EXCAVATION_MATERIALS.CHODNIK) {
    return EXCAVATION_SUBTYPES_CHODNIK;
  }
  if (material === EXCAVATION_MATERIALS.VOZOVKA) {
    return EXCAVATION_SUBTYPES_VOZOVKA;
  }
  if (material === EXCAVATION_MATERIALS.POVRCH_PODVRT) {
    return EXCAVATION_SUBTYPES_PODVRT;
  }
  return [];
}

/** True pokud materiál vyžaduje výběr podtypu */
export function materialRequiresSubtype(material: ExcavationMaterial): boolean {
  return !EXCAVATION_MATERIALS_WITHOUT_SUBTYPE.includes(material);
}

/** Český název materiálu */
export function getExcavationMaterialName(material: string): string {
  return EXCAVATION_MATERIAL_NAMES[material] ?? material;
}

/** Český název podtypu */
export function getExcavationSubtypeName(subtype: string): string {
  return EXCAVATION_SUBTYPE_NAMES[subtype] ?? subtype;
}

// =============================================================================
// ODSTRANĚNÍ NAD RÁMEC (Nad rámec – odstranění povrchu)
// =============================================================================

export const REMOVAL_BEYOND_SCOPE = {
  CHODNIK_ASFALT: 'CHODNIK_ASFALT',
  VOZOVKA_ASFALT: 'VOZOVKA_ASFALT',
  BOURANI_BETON_ZAKLADY: 'BOURANI_BETON_ZAKLADY',
  ROZVOZ_ZEMINA: 'ROZVOZ_ZEMINA',
} as const;

export type RemovalBeyondScope =
  (typeof REMOVAL_BEYOND_SCOPE)[keyof typeof REMOVAL_BEYOND_SCOPE];

export const REMOVAL_BEYOND_SCOPE_NAMES: Record<string, string> = {
  [REMOVAL_BEYOND_SCOPE.CHODNIK_ASFALT]: 'Chodník asfalt',
  [REMOVAL_BEYOND_SCOPE.VOZOVKA_ASFALT]: 'Vozovka asfalt',
  [REMOVAL_BEYOND_SCOPE.BOURANI_BETON_ZAKLADY]: 'Bourání betonových základů',
  [REMOVAL_BEYOND_SCOPE.ROZVOZ_ZEMINA]: 'Rozvoz zeminy (hrabičkování)',
};

/** Nad rámec volitelné per řádek povrchu (rozbalí se tlačítkem „Nad rámec“). */
export const PER_ROW_REMOVAL_BEYOND_SCOPE = [
  REMOVAL_BEYOND_SCOPE.CHODNIK_ASFALT,
  REMOVAL_BEYOND_SCOPE.VOZOVKA_ASFALT,
] as const;

/** Vždy viditelné položky pod tabulkou povrchů (bez skládání). */
export const ALWAYS_VISIBLE_REMOVAL_BEYOND_SCOPE = [
  REMOVAL_BEYOND_SCOPE.BOURANI_BETON_ZAKLADY,
  REMOVAL_BEYOND_SCOPE.ROZVOZ_ZEMINA,
] as const;

/** Jednotka množství: m² pro asfalt/rozvoz, m³ pro bourání betonových základů */
export function removalBeyondScopeUnit(code: RemovalBeyondScope): 'm²' | 'm³' {
  return code === REMOVAL_BEYOND_SCOPE.BOURANI_BETON_ZAKLADY ? 'm³' : 'm²';
}

/** Šířka (m) už započtená v ceně výkopu – u asfaltu (m²) a u obnovy/práce nad rámec (zakopaný) se platí za (šířka - 0,35) * délka. */
export const REMOVAL_BEYOND_SCOPE_INCLUDED_WIDTH_M = 0.35;

/** Výchozí šířka / hloubka rýhy pro UI prefilled hodnoty a billing fallback (výkop / obnova / rozvoz). */
export const EXCAVATION_DEFAULT_WIDTH_M = 0.35;
export const EXCAVATION_DEFAULT_DEPTH_M = 0.7;

/** U bourání betonových základů (m³) uživatel zadává hloubku; v režimu volume i u rozvozu zemina (m³). */
export function removalBeyondScopeNeedsDepth(
  code: RemovalBeyondScope,
  mode: ExcavationPricingMode = 'linear',
): boolean {
  if (code === REMOVAL_BEYOND_SCOPE.BOURANI_BETON_ZAKLADY) return true;
  if (mode === 'volume' && code === REMOVAL_BEYOND_SCOPE.ROZVOZ_ZEMINA) return true;
  return false;
}

/**
 * Vypočte množství nad rámec (m² nebo m³) ze šířky, délky trasy a u betonu hloubky.
 * Asfalt (m²): (šířka - 0,35) * délka (0,35 m je v základní ceně).
 * Bourání betonových základů (m³): šířka * délka * hloubka (celé rozměry, bez odečtu).
 * Vrací 0 pokud šířka <= 0 nebo délka <= 0; u asfaltu také pokud šířka <= 0,35; u betonu pokud hloubka <= 0.
 */
export function computeRemovalBeyondScopeQuantity(
  code: RemovalBeyondScope,
  widthM: number,
  lengthM: number,
  depthM?: number,
): number {
  if (lengthM <= 0) return 0;
  if (code === REMOVAL_BEYOND_SCOPE.BOURANI_BETON_ZAKLADY) {
    const d = depthM ?? 0;
    if (widthM <= 0 || d <= 0) return 0;
    return Math.round(widthM * lengthM * d * 100) / 100;
  }
  const effectiveWidth = widthM - REMOVAL_BEYOND_SCOPE_INCLUDED_WIDTH_M;
  if (effectiveWidth <= 0) return 0;
  return Math.round(effectiveWidth * lengthM * 100) / 100;
}

/** Prohloubení výkopu – kroky po 10 cm (0, 10, 20, … max 200) */
export const EXCAVATION_DEEPENING_STEP_CM = 10;
export const EXCAVATION_DEEPENING_MAX_CM = 200;

export function getExcavationDeepeningOptionsCm(): number[] {
  const out: number[] = [];
  for (let cm = 0; cm <= EXCAVATION_DEEPENING_MAX_CM; cm += EXCAVATION_DEEPENING_STEP_CM) {
    out.push(cm);
  }
  return out;
}

// =============================================================================
// ZAKOPANY – Práce nad rámec a Obnova (při přechodu na Zakopaný, vše m²)
// =============================================================================

/**
 * Vypočte m² pro Práce nad rámec / Obnova při Zakopaném ze šířky a délky trasy.
 * Stejný princip jako u výkopu: (šířka - 0,35) × délka. Vrací 0 pokud šířka ≤ 0,35 nebo délka ≤ 0.
 */
export function computeZakopanyM2FromWidth(widthM: number, lengthM: number): number {
  const effectiveWidth = widthM - REMOVAL_BEYOND_SCOPE_INCLUDED_WIDTH_M;
  if (effectiveWidth <= 0 || lengthM <= 0) return 0;
  return Math.round(effectiveWidth * lengthM * 100) / 100;
}

/** Práce nad rámec – při přechodu na VYKOP_ZAKOPANY, jednotka m² */
export const ZAKOPANY_WORK_BEYOND_SCOPE = {
  DLAZBA: 'DLAZBA',
  MOZAIKA: 'MOZAIKA',
  ASFALT: 'ASFALT',
} as const;

export type ZakopanyWorkBeyondScope = (typeof ZAKOPANY_WORK_BEYOND_SCOPE)[keyof typeof ZAKOPANY_WORK_BEYOND_SCOPE];

export const ZAKOPANY_WORK_BEYOND_SCOPE_NAMES: Record<string, string> = {
  [ZAKOPANY_WORK_BEYOND_SCOPE.DLAZBA]: 'Dlažba',
  [ZAKOPANY_WORK_BEYOND_SCOPE.MOZAIKA]: 'Mozaika',
  [ZAKOPANY_WORK_BEYOND_SCOPE.ASFALT]: 'Asfalt',
};

/** Obnova – při přechodu na Zakopaný, jednotka m² */
export const ZAKOPANY_OBNOVA = {
  BETON: 'BETON',
  ROZVOZ_ZEMINA: 'ROZVOZ_ZEMINA',
} as const;

export type ZakopanyObnova = (typeof ZAKOPANY_OBNOVA)[keyof typeof ZAKOPANY_OBNOVA];

export const ZAKOPANY_OBNOVA_NAMES: Record<string, string> = {
  [ZAKOPANY_OBNOVA.BETON]: 'Beton',
  [ZAKOPANY_OBNOVA.ROZVOZ_ZEMINA]: 'Rozvoz zeminy (hrabičkování)',
};

/** Jedna položka Práce nad rámec / Obnova při Zakopaném (m² = (šířka − 0,35) × délka). */
export interface ZakopanySurfaceRow {
  code: string;
  lengthM: number;
  widthM: number;
  m2?: number;
}

export function isZakopanyWorkBeyondScopeCode(code: string): boolean {
  return (Object.values(ZAKOPANY_WORK_BEYOND_SCOPE) as string[]).includes(code);
}

export function isZakopanyObnovaCode(code: string): boolean {
  return (Object.values(ZAKOPANY_OBNOVA) as string[]).includes(code);
}

/**
 * Per-řádkové Nad rámec v 1. fázi (Výkop):
 * výhradně odstranění asfaltu (CHODNIK_ASFALT, VOZOVKA_ASFALT).
 * Finální obnova (dlažba, mozaika, asfalt, beton) se zadává ve 2. fázi (Obnova).
 */
export const PER_ROW_BEYOND_SCOPE_OPTIONS = [
  REMOVAL_BEYOND_SCOPE.CHODNIK_ASFALT,
  REMOVAL_BEYOND_SCOPE.VOZOVKA_ASFALT,
] as const;

/**
 * Položky Nad rámec obnova ve 2. fázi (Obnova / Finálka) v délkovém ceníku (m² = (šířka − 0,35) × délka).
 */
export const OBNOVA_BEYOND_SCOPE_OPTIONS = [
  ZAKOPANY_WORK_BEYOND_SCOPE.DLAZBA,
  ZAKOPANY_WORK_BEYOND_SCOPE.MOZAIKA,
  ZAKOPANY_WORK_BEYOND_SCOPE.ASFALT,
  ZAKOPANY_OBNOVA.BETON,
] as const;

export type ObnovaBeyondScopeCode = (typeof OBNOVA_BEYOND_SCOPE_OPTIONS)[number];

/**
 * UI popisky pro dropdown „Nad rámec“ (web + Android). Klíče API / ceník zůstávají beze změny.
 * Vzor: [typ povrchu] [materiál] [akce], např. „Chodník asfalt odstranění“, „Dlažba obnova“.
 */
const PER_ROW_BEYOND_SCOPE_UI_LABELS: Record<string, string> = {
  [REMOVAL_BEYOND_SCOPE.CHODNIK_ASFALT]: 'Chodník asfalt odstranění',
  [REMOVAL_BEYOND_SCOPE.VOZOVKA_ASFALT]: 'Vozovka asfalt odstranění',
  [ZAKOPANY_WORK_BEYOND_SCOPE.DLAZBA]: 'Dlažba obnova',
  [ZAKOPANY_WORK_BEYOND_SCOPE.MOZAIKA]: 'Mozaika obnova',
  [ZAKOPANY_WORK_BEYOND_SCOPE.ASFALT]: 'Asfalt obnova',
  [ZAKOPANY_OBNOVA.BETON]: 'Beton obnova',
};

/** Český název položky Nad rámec pro picker (asfalt odstranění i finální práce / obnova). */
export function getPerRowBeyondScopeLabel(code: string): string {
  return PER_ROW_BEYOND_SCOPE_UI_LABELS[code] ?? code;
}

/** Dlažba / mozaika / asfalt / beton — ceník Finální (PHASE.2), ne odstranění asfaltu. */
export function isFinalPhasePerRowBeyondScopeCode(code: string): boolean {
  return isZakopanyWorkBeyondScopeCode(code) || code === ZAKOPANY_OBNOVA.BETON;
}

function parseBeyondScopeWidthM(raw: unknown): number | undefined {
  if (raw == null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * Per-povrch Nad rámec: preferuje `removalBeyondScopeRows[]`, fallback na legacy skaláře.
 * Neúplné řádky (bez kódu / šířky) se vynechají.
 */
export function getExcavationSurfaceBeyondScopeRows(
  seg:
    | Pick<
        ExcavationSurfaceSegment,
        'removalBeyondScope' | 'removalBeyondScopeWidthM' | 'removalBeyondScopeRows'
      >
    | Record<string, unknown>
    | null
    | undefined,
): ExcavationBeyondScopeRow[] {
  if (!seg || typeof seg !== 'object') return [];
  const rec = seg as Record<string, unknown>;
  const rowsRaw = rec['removalBeyondScopeRows'];
  if (Array.isArray(rowsRaw) && rowsRaw.length > 0) {
    const out: ExcavationBeyondScopeRow[] = [];
    for (const raw of rowsRaw) {
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
      const row = raw as Record<string, unknown>;
      const code = typeof row['code'] === 'string' ? row['code'].trim() : '';
      const widthM = parseBeyondScopeWidthM(row['widthM']);
      if (!code || widthM == null) continue;
      out.push({ code, widthM });
    }
    if (out.length > 0) return out;
  }
  const legacyCode =
    typeof rec['removalBeyondScope'] === 'string' ? rec['removalBeyondScope'].trim() : '';
  const legacyWidth = parseBeyondScopeWidthM(rec['removalBeyondScopeWidthM']);
  if (legacyCode && legacyWidth != null) {
    return [{ code: legacyCode, widthM: legacyWidth }];
  }
  return [];
}

/**
 * Zapíše kompletní Nad rámec řádky na segment: pole `removalBeyondScopeRows` + zrcadlo
 * prvního řádku do legacy skalářů (Android / starší čtenáři).
 */
export function applyExcavationSurfaceBeyondScopeRows(
  seg: ExcavationSurfaceSegment,
  rows: ExcavationBeyondScopeRow[],
): void {
  delete seg.removalBeyondScope;
  delete seg.removalBeyondScopeWidthM;
  delete seg.removalBeyondScopeRows;
  const complete = rows
    .map((r) => ({
      code: (r.code ?? '').trim(),
      widthM: typeof r.widthM === 'number' && Number.isFinite(r.widthM) ? r.widthM : 0,
    }))
    .filter((r) => r.code && r.widthM > 0);
  if (complete.length === 0) return;
  seg.removalBeyondScopeRows = complete;
  seg.removalBeyondScope = complete[0].code;
  seg.removalBeyondScopeWidthM = complete[0].widthM;
}

// =============================================================================
// Ceník nový (volume mode) — profily rýh, m²/m³ výpočty a Obnova povrchy
// =============================================================================

/** Profil rýhy pro výpočet m²/m³ z délky úseku (parita starý ceník — šířky v metrech). */
export interface ExcavationTrenchProfile {
  /** Šířka tělesa rýhy (m). */
  widthM: number;
  /** Hloubka tělesa rýhy (m). */
  depthM: number;
  /** Billovatelná šířka vršku pro m² položky (m). */
  surfaceWidthM: number;
}

/** Mapuje legacy materiál + podtyp na konsolidovaný billing bucket. */
export function resolveExcavationSurfaceBucket(
  material: string,
  subtype?: string | null,
): ExcavationSurfaceBucket | undefined {
  const mat = (material ?? '').trim();
  const sub = (subtype ?? '').trim();
  if (!mat) return undefined;

  if (mat === EXCAVATION_MATERIALS.VOLNY_TEREN) {
    return EXCAVATION_SURFACE_BUCKETS.VOLNY_TEREN;
  }
  if (mat === EXCAVATION_MATERIALS.KACIREK) {
    return EXCAVATION_SURFACE_BUCKETS.KACIREK;
  }
  if (mat === EXCAVATION_MATERIALS.CHODNIK) {
    if (sub === 'ASFALT' || sub === 'BETON') return EXCAVATION_SURFACE_BUCKETS.ASFALT_A_BETON;
    if (sub === 'DLAZBA') return EXCAVATION_SURFACE_BUCKETS.DLAZBA;
    if (sub === 'MOZAIKA') return EXCAVATION_SURFACE_BUCKETS.MOZAIKA;
    return undefined;
  }
  if (mat === EXCAVATION_MATERIALS.VOZOVKA) {
    if (sub === 'ASFALT' || sub === 'BETON') return EXCAVATION_SURFACE_BUCKETS.ASFALT_A_BETON;
    if (sub === 'DLAZBA') return EXCAVATION_SURFACE_BUCKETS.DLAZBA;
    if (sub === 'STERK') return EXCAVATION_SURFACE_BUCKETS.STERK;
    return undefined;
  }
  return undefined;
}

/** Profil rýhy pro výpočet množství z délky úseku. */
export function getExcavationTrenchProfile(
  material: string,
  subtype?: string | null,
): ExcavationTrenchProfile {
  const mat = (material ?? '').trim();
  const sub = (subtype ?? '').trim();

  if (mat === EXCAVATION_MATERIALS.VOLNY_TEREN) {
    if (sub === '35x100') {
      return { widthM: 0.35, depthM: 1.0, surfaceWidthM: 0.35 };
    }
    return { widthM: 0.35, depthM: 0.7, surfaceWidthM: 0.35 };
  }
  if (mat === EXCAVATION_MATERIALS.CHODNIK) {
    return { widthM: 0.35, depthM: 0.5, surfaceWidthM: 0.5 };
  }
  if (mat === EXCAVATION_MATERIALS.VOZOVKA) {
    return { widthM: 0.4, depthM: 1.1, surfaceWidthM: 0.6 };
  }
  if (mat === EXCAVATION_MATERIALS.KACIREK) {
    return { widthM: 0.35, depthM: 0.5, surfaceWidthM: 0.5 };
  }
  return { widthM: 0.35, depthM: 0.7, surfaceWidthM: 0.35 };
}

function roundQty(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** m³ z jednoho úseku povrchu (volný terén / těleso rýhy). */
export function computeExcavationSegmentM3(
  material: string,
  subtype: string | undefined,
  lengthM: number,
): number {
  if (lengthM <= 0) return 0;
  const profile = getExcavationTrenchProfile(material, subtype);
  return roundQty(lengthM * profile.widthM * profile.depthM);
}

/** m² vršku z jednoho úseku (zpevněný povrch). */
export function computeExcavationSegmentM2(
  material: string,
  subtype: string | undefined,
  lengthM: number,
): number {
  if (lengthM <= 0) return 0;
  const profile = getExcavationTrenchProfile(material, subtype);
  return roundQty(lengthM * profile.surfaceWidthM);
}

export type ExcavationBucketSegment = Pick<
  ExcavationSurfaceSegment,
  | 'excavationMaterial'
  | 'excavationMaterialSubtype'
  | 'lengthM'
  | 'widthM'
  | 'depthM'
  | 'surfaceLengthM'
  | 'surfaceWidthM'
  | 'volumeLengthM'
  | 'volumeWidthM'
  | 'm2'
  | 'm3'
  | 'surfaceBucket'
>;

/** m³ z úseku — explicitní m³, pak šířka×délka×hloubka, pak výchozí šířka/hloubka. */
export function computeSegmentQuantityM3(
  _material: string,
  _subtype: string | undefined,
  lengthM: number,
  widthM?: number,
  depthM?: number,
  m3?: number,
): number {
  if (typeof m3 === 'number' && m3 > 0) return roundQty(m3);
  if (lengthM <= 0) return 0;
  const w = widthM != null && widthM > 0 ? widthM : EXCAVATION_DEFAULT_WIDTH_M;
  const d = depthM != null && depthM > 0 ? depthM : EXCAVATION_DEFAULT_DEPTH_M;
  return roundQty(lengthM * w * d);
}

/** m² z úseku — explicitní m², pak šířka×délka, pak výchozí šířka. */
export function computeSegmentQuantityM2(
  _material: string,
  _subtype: string | undefined,
  lengthM: number,
  widthM?: number,
  m2?: number,
): number {
  if (typeof m2 === 'number' && m2 > 0) return roundQty(m2);
  if (lengthM <= 0) return 0;
  const w = widthM != null && widthM > 0 ? widthM : EXCAVATION_DEFAULT_WIDTH_M;
  return roundQty(lengthM * w);
}

function resolveSegmentBucket(seg: ExcavationBucketSegment): ExcavationSurfaceBucket | undefined {
  const explicit = (seg.surfaceBucket ?? '').trim() as ExcavationSurfaceBucket | '';
  if (explicit) return explicit;
  return resolveExcavationSurfaceBucket(seg.excavationMaterial, seg.excavationMaterialSubtype);
}

function segmentMatchesBucket(seg: ExcavationBucketSegment, bucket: ExcavationSurfaceBucket): boolean {
  return resolveSegmentBucket(seg) === bucket;
}

/**
 * Celkový objem výkopu / hlíny (m³) ze všech segmentů Výkopu v režimu `volume`.
 * - U `VOLNY_TEREN`: `m3` nebo `(volumeLengthM ?? lengthM) × (volumeWidthM ?? widthM) × depthM`.
 * - U zpevněných povrchů (`ASFALT_A_BETON`, `DLAZBA`, …): započte se objem hlíny pod povrchem,
 *   pokud má segment zadáno `m3 > 0`, `depthM > 0` nebo `volumeLengthM > 0`.
 */
export function computeVolumeExcavationEarthM3(
  segments: ReadonlyArray<ExcavationBucketSegment>,
): number {
  let total = 0;
  for (const seg of segments) {
    const segBucket = resolveSegmentBucket(seg);
    const mat = seg.excavationMaterial;
    const sub = seg.excavationMaterialSubtype;
    if (segBucket === EXCAVATION_SURFACE_BUCKETS.VOLNY_TEREN) {
      const vLen =
        seg.volumeLengthM != null && seg.volumeLengthM > 0 ? seg.volumeLengthM : (seg.lengthM ?? 0);
      const vWid =
        seg.volumeWidthM != null && seg.volumeWidthM > 0 ? seg.volumeWidthM : seg.widthM;
      total += computeSegmentQuantityM3(mat, sub, vLen, vWid, seg.depthM, seg.m3);
    } else if (
      (typeof seg.m3 === 'number' && seg.m3 > 0) ||
      (seg.depthM != null && seg.depthM > 0) ||
      (seg.volumeLengthM != null && seg.volumeLengthM > 0)
    ) {
      const vLen =
        seg.volumeLengthM != null && seg.volumeLengthM > 0 ? seg.volumeLengthM : (seg.lengthM ?? 0);
      const vWid =
        seg.volumeWidthM != null && seg.volumeWidthM > 0 ? seg.volumeWidthM : seg.widthM;
      total += computeSegmentQuantityM3(mat, sub, vLen, vWid, seg.depthM, seg.m3);
    }
  }
  return roundQty(total);
}

/**
 * Plocha krytu zpevněného povrchu (m²) pro daný `bucket` v režimu `volume`.
 * Preferuje `m2`, pak `surfaceLengthM × surfaceWidthM`, fallback `lengthM × widthM`.
 */
export function computeVolumeExcavationSurfaceM2(
  bucket: ExcavationSurfaceBucket,
  segments: ReadonlyArray<ExcavationBucketSegment>,
): number {
  if (bucket === EXCAVATION_SURFACE_BUCKETS.VOLNY_TEREN) return 0;
  let total = 0;
  for (const seg of segments) {
    if (!segmentMatchesBucket(seg, bucket)) continue;
    const sLen =
      seg.surfaceLengthM != null && seg.surfaceLengthM > 0 ? seg.surfaceLengthM : (seg.lengthM ?? 0);
    if (sLen <= 0 && !(typeof seg.m2 === 'number' && seg.m2 > 0)) continue;
    const sWid =
      seg.surfaceWidthM != null && seg.surfaceWidthM > 0 ? seg.surfaceWidthM : seg.widthM;
    total += computeSegmentQuantityM2(seg.excavationMaterial, seg.excavationMaterialSubtype, sLen, sWid, seg.m2);
  }
  return roundQty(total);
}

/**
 * Vytvoří výchozí řádky obnovy zpevněných povrchů (`ObnovaSurfaceSegment[]`) z 1. fáze (Výkop).
 * Obnova objemu (zemina m³) je vždy shodná s objemem výkopu a do povrchových řádků se nekopíruje.
 */
export function deriveDefaultObnovaSurfacesFromVykop(
  segments: ReadonlyArray<ExcavationBucketSegment> | undefined | null,
): ObnovaSurfaceSegment[] {
  if (!segments?.length) return [];
  const out: ObnovaSurfaceSegment[] = [];
  for (const seg of segments) {
    const bucket = resolveSegmentBucket(seg);
    if (!bucket || bucket === EXCAVATION_SURFACE_BUCKETS.VOLNY_TEREN) continue;
    const sLen =
      seg.surfaceLengthM != null && seg.surfaceLengthM > 0 ? seg.surfaceLengthM : (seg.lengthM ?? 0);
    if (sLen <= 0) continue;
    const sWid =
      seg.surfaceWidthM != null && seg.surfaceWidthM > 0
        ? seg.surfaceWidthM
        : seg.widthM != null && seg.widthM > 0
          ? seg.widthM
          : EXCAVATION_DEFAULT_WIDTH_M;
    out.push({
      surfaceBucket: bucket,
      lengthM: sLen,
      widthM: sWid,
      ...(typeof seg.m2 === 'number' && seg.m2 > 0 ? { m2: seg.m2 } : {}),
      phaseScope: 'obnova',
    });
  }
  return out;
}

/** Součet m²/m³ pro billing bucket ze segmentů `excavationSurfaces` / `obnovaSurfaces`. */
export function computeExcavationBucketQuantity(
  bucket: ExcavationSurfaceBucket,
  unit: 'm2' | 'm3',
  segments: ReadonlyArray<ExcavationBucketSegment>,
): number {
  if (unit === 'm3' && bucket === EXCAVATION_SURFACE_BUCKETS.VOLNY_TEREN) {
    return computeVolumeExcavationEarthM3(segments);
  }
  if (unit === 'm2' && bucket !== EXCAVATION_SURFACE_BUCKETS.VOLNY_TEREN) {
    return computeVolumeExcavationSurfaceM2(bucket, segments);
  }
  let total = 0;
  for (const seg of segments) {
    const len = seg.lengthM ?? 0;
    if (len <= 0) continue;
    if (!segmentMatchesBucket(seg, bucket)) continue;
    const mat = seg.excavationMaterial;
    const sub = seg.excavationMaterialSubtype;
    if (unit === 'm3') {
      total += computeSegmentQuantityM3(mat, sub, len, seg.widthM, seg.depthM, seg.m3);
    } else {
      total += computeSegmentQuantityM2(mat, sub, len, seg.widthM, seg.m2);
    }
  }
  return roundQty(total);
}

/** Rozvoz zemina — m³ = šířka × délka × hloubka (chybějící hloubka → [EXCAVATION_DEFAULT_DEPTH_M]). */
export function computeRozvozZeminaM3(
  widthM: number,
  lengthM: number,
  depthM?: number,
): number {
  if (widthM <= 0 || lengthM <= 0) return 0;
  const d = depthM != null && depthM > 0 ? depthM : EXCAVATION_DEFAULT_DEPTH_M;
  return roundQty(widthM * lengthM * d);
}

/** Množství bourání / rozvoz — rozvoz v režimu volume používá m³ helper, jinak dle kódu. */
export function computeSpecialRemovalQuantity(
  code: string,
  widthM: number,
  lengthM: number,
  depthM?: number,
  mode: ExcavationPricingMode = 'volume',
): number {
  if (mode === 'volume' && code === REMOVAL_BEYOND_SCOPE.ROZVOZ_ZEMINA) {
    return computeRozvozZeminaM3(widthM, lengthM, depthM);
  }
  if (code === REMOVAL_BEYOND_SCOPE.BOURANI_BETON_ZAKLADY) {
    const d = depthM != null && depthM > 0 ? depthM : EXCAVATION_DEFAULT_DEPTH_M;
    return computeRemovalBeyondScopeQuantity(code, widthM, lengthM, d);
  }
  return computeRemovalBeyondScopeQuantity(code as RemovalBeyondScope, widthM, lengthM, depthM);
}

export function specialRemovalUnit(code: string, mode: ExcavationPricingMode = 'volume'): 'm²' | 'm³' {
  if (code === REMOVAL_BEYOND_SCOPE.BOURANI_BETON_ZAKLADY) {
    return 'm³';
  }
  if (mode === 'volume' && code === REMOVAL_BEYOND_SCOPE.ROZVOZ_ZEMINA) {
    return 'm³';
  }
  return 'm²';
}

/** Reprezentativní materiál pro výpočet m²/m³ z délky a bucketu (obnovaSurfaces bez materiálu). */
export function representativeSurfaceSegmentForBucket(
  bucket: ExcavationSurfaceBucket,
  lengthM: number,
): Pick<ExcavationSurfaceSegment, 'excavationMaterial' | 'excavationMaterialSubtype' | 'lengthM'> {
  switch (bucket) {
    case EXCAVATION_SURFACE_BUCKETS.VOLNY_TEREN:
      return { excavationMaterial: EXCAVATION_MATERIALS.VOLNY_TEREN, excavationMaterialSubtype: '35x70', lengthM };
    case EXCAVATION_SURFACE_BUCKETS.ASFALT_A_BETON:
      return { excavationMaterial: EXCAVATION_MATERIALS.CHODNIK, excavationMaterialSubtype: 'ASFALT', lengthM };
    case EXCAVATION_SURFACE_BUCKETS.DLAZBA:
      return { excavationMaterial: EXCAVATION_MATERIALS.CHODNIK, excavationMaterialSubtype: 'DLAZBA', lengthM };
    case EXCAVATION_SURFACE_BUCKETS.MOZAIKA:
      return { excavationMaterial: EXCAVATION_MATERIALS.CHODNIK, excavationMaterialSubtype: 'MOZAIKA', lengthM };
    case EXCAVATION_SURFACE_BUCKETS.KACIREK:
      return { excavationMaterial: EXCAVATION_MATERIALS.KACIREK, lengthM };
    case EXCAVATION_SURFACE_BUCKETS.STERK:
      return { excavationMaterial: EXCAVATION_MATERIALS.VOZOVKA, excavationMaterialSubtype: 'STERK', lengthM };
    default:
      return { excavationMaterial: EXCAVATION_MATERIALS.VOLNY_TEREN, excavationMaterialSubtype: '35x70', lengthM };
  }
}

/** Obnova povrchu — jeden řádek z modalu Obnova (dual Povrchy). */
export interface ObnovaSurfaceSegment {
  surfaceBucket: ExcavationSurfaceBucket;
  lengthM: number;
  widthM?: number;
  depthM?: number;
  m2?: number;
  m3?: number;
  phaseScope?: ExcavationSurfacePhaseScope;
}

/** Všechny billing buckety pro picker (Výkop / Obnova). */
export const ALL_SURFACE_BUCKETS = Object.values(
  EXCAVATION_SURFACE_BUCKETS,
) as ExcavationSurfaceBucket[];

/** Bourání — modal Výkop (PHASE.1 billing). */
export const VYKOP_SPECIAL_REMOVAL_CODES = [REMOVAL_BEYOND_SCOPE.BOURANI_BETON_ZAKLADY] as const;

/** Rozvoz m³ / hrabičkování — modal Obnova (PHASE.2 billing); platí linear i volume. */
export const OBNOVA_SPECIAL_REMOVAL_CODES = [REMOVAL_BEYOND_SCOPE.ROZVOZ_ZEMINA] as const;

/**
 * Speciální řádky (bourání / rozvoz) pro UI podle fáze.
 * Stejné pro `linear` i `volume` — volume Obnova musí dál ukazovat ROZVOZ_ZEMINA.
 */
export function specialRemovalCodesForPhase(
  phase: 'vykop' | 'obnova',
): readonly RemovalBeyondScope[] {
  return phase === 'obnova' ? OBNOVA_SPECIAL_REMOVAL_CODES : VYKOP_SPECIAL_REMOVAL_CODES;
}

export function getExcavationSurfaceBucketName(bucket: string): string {
  return EXCAVATION_SURFACE_BUCKET_NAMES[bucket as ExcavationSurfaceBucket] ?? bucket;
}

type SurfaceRowLike = {
  surfaceBucket?: string;
  excavationMaterial?: string;
  excavationMaterialSubtype?: string;
  lengthM?: number | null;
};

/**
 * Alespoň jeden platný výkopový řádek: délka ≥ 0 + bucket nebo materiál (+ podtyp pokud vyžadován).
 * Parita mobile `hasValidSurfaceRow`.
 */
export function hasValidSurfaceRow(rows: ReadonlyArray<SurfaceRowLike>): boolean {
  for (const row of rows) {
    const lengthM = row.lengthM;
    if (lengthM == null || Number.isNaN(lengthM) || lengthM < 0) continue;
    const bucket = (row.surfaceBucket ?? '').trim();
    if (bucket) return true;
    const material = (row.excavationMaterial ?? '').trim();
    if (!material) continue;
    if (materialRequiresSubtype(material as ExcavationMaterial) && !(row.excavationMaterialSubtype ?? '').trim()) {
      continue;
    }
    return true;
  }
  return false;
}

/** Platný řádek obnovy: bucket + délka ≥ 0. */
export function hasValidObnovaSurfaceRow(
  rows: ReadonlyArray<Pick<ObnovaSurfaceSegment, 'surfaceBucket' | 'lengthM'>>,
): boolean {
  for (const row of rows) {
    const lengthM = row.lengthM;
    if (lengthM == null || Number.isNaN(lengthM) || lengthM < 0) continue;
    if ((row.surfaceBucket ?? '').trim()) return true;
  }
  return false;
}

/** Obnova povinná data: bucket řádky nebo vyplněný rozvoz m³. */
export function hasValidObnovaData(
  obnovaRows: ReadonlyArray<Pick<ObnovaSurfaceSegment, 'surfaceBucket' | 'lengthM'>>,
  rozvozRows: ReadonlyArray<Pick<SpecialRemovalRow, 'code' | 'lengthM' | 'widthM' | 'depthM'>> = [],
): boolean {
  if (hasValidObnovaSurfaceRow(obnovaRows)) return true;
  for (const row of rozvozRows) {
    if (row.code !== REMOVAL_BEYOND_SCOPE.ROZVOZ_ZEMINA) continue;
    const len = row.lengthM;
    const width = row.widthM;
    const depth = row.depthM;
    if (len != null && len > 0 && width != null && width > 0 && depth != null && depth > 0) {
      return true;
    }
  }
  return false;
}
